<template>
  <div class="rest-settings">
    <div class="d-flex justify-content-between align-items-center my-3">
      <h2>{{ $t('restSettings.restSettingsTitle') }}
        <span v-if="this.restaurantAreLoading" class="d-inline-block ms-1 spinner-border spinner-border-sm h4 fw-normal text-success" role="status">
          <span class="visually-hidden">Loading...</span>
        </span>
      </h2>
        <button @click="openModalRemoveRestaurant()"
            class="btn btn-outline-danger pt-0 p-1">
          <svg width="1rem" height="1rem" fill="currentColor">
            <use xlink:href="../../assets/ico/bootstrap-icons.svg#trash3-fill"/>
          </svg>
        </button>
    </div>
    <div class="row my-3">
      <div class="col-12 py-2 mb-3 bg-secondary bg-opacity-25">{{ $t('restSettings.sections.main') }}</div>
      <div class="col-12 col-sm-6 mb-3">
        <label for="restTitle" class="form-label">{{ $t('restSettings.restTitle') }}<span class="small text-danger">*</span></label>
        <input v-model.trim="restaurant.title" type="text" class="form-control" id="restTitle">
      </div>
      <div class="col-12 col-sm-6 mb-3">
        <div class="form-check form-switch">
          <input
              v-model="restaurant.is_available"
              class="form-check-input"
              type="checkbox" role="switch" id="prodIsAvailable" aria-describedby="prodIsAvailableHelp" checked>
          <label v-if="restaurant.is_available" class="form-check-label" for="prodIsAvailable">{{ $t('restSettings.restaurantIsAvailable') }}</label>
          <label v-else class="form-check-label" for="prodIsAvailable">{{ $t('restSettings.restaurantClosed') }}</label>
          <div id="prodIsAvailableHelp" class="form-text">{{ $t('restSettings.restaurantCloseDescriptionMessage') }}</div>
        </div>
      </div>
      <div class="col-12 col-sm-6 mb-3">
        <label for="restDescription" class="form-label">{{ $t('restSettings.description') }} <span class="small text-danger">*</span></label>
        <textarea v-model.trim="restaurant.description" class="form-control" id="restDescription" rows="3" aria-describedby="restDescriptionHelp"></textarea>
        <div id="restDescriptionHelp" class="form-text">{{ $t('restSettings.descriptionMessage') }}</div>
      </div>
      <div class="col-12 col-sm-6 mb-3">
          <label for="restCuisine" class="form-label">{{ $t('restSettings.cuisine') }}</label>
          <input v-model.trim="cuisineStr" :placeholder="$t('restSettings.cuisineExample')" type="text" class="form-control" id="restCuisine">
      </div>
    </div>
    <div class="row my-3">
      <div class="col-12 py-2 mb-3 bg-secondary bg-opacity-25">{{ $t('restSettings.sections.contacts') }}</div>
      <div class="col-12 col-sm-6 mb-3">
        <div class="row">
          <div class="col-12 col-xl-6 mb-3">
            <label for="restRegion" class="form-label">
              {{ $t('restSettings.region') }}
            </label>
            <input v-model.trim="restaurant.address_settings.region" type="text" class="form-control" id="restRegion">
          </div>
          <div class="col-12 col-xl-6 mb-3">
            <label for="restCity" class="form-label">
              {{ $t('restSettings.city') }}
              <span class="small text-danger">*</span>
            </label>
            <input v-model.trim="restaurant.address_settings.city" type="text" class="form-control" id="restCity">
          </div>
          <div class="col-12 col-xl-6 mb-3">
            <label for="restAddress" class="form-label">
              {{ $t('restSettings.address') }}
              <span class="small text-danger">*</span>
            </label>
            <input v-model.trim="restaurant.address_settings.address" type="text" class="form-control" id="restAddress" :placeholder="$t('restSettings.addressPlaceholder')">
          </div>
          <div class="col-12 col-xl-6 mb-3">
            <label for="restPhone" class="form-label">
              {{ $t('restSettings.phone') }}
              <span class="small text-danger">*</span>
            </label>
            <input v-model.trim="restaurant.phone" type="text" class="form-control" id="restPhone">
          </div>
        </div>
      </div>
      <div class="col-12 col-sm-6 mb-3">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <p class="mb-1">{{ $t('restSettings.schedule') }} <span class="small text-danger">*</span></p>
            <div class="mb-2 form-check">
              <input v-model="temp.dayAndNight" type="checkbox" class="form-check-input" id="roundTheCheck">
              <label class="form-check-label small" for="roundTheCheck">{{ $t('restSettings.aroundTheClock') }}</label>
            </div>
          </div>
          <div>
            <div class="btn-group btn-group-sm" role="group">
              <input type="radio" class="btn-check" id="openingTimeSimple" autocomplete="off"
                     v-model="temp.openingTime" value="simple">
              <label class="btn btn-outline-secondary" for="openingTimeSimple">{{ $t('restSettings.openingTimeSimple') }}</label>
              <input type="radio" class="btn-check" id="openingTimeExtended" autocomplete="off"
                     :disabled="this.temp.dayAndNight"
                     v-model="temp.openingTime" value="extended">
              <label class="btn btn-outline-secondary" for="openingTimeExtended">{{ $t('restSettings.openingTimeExtended') }}</label>
            </div>
          </div>
        </div>
        <div v-if="temp.openingTime === 'simple'" class="d-flex gap-2">
          <div class="flex-fill d-flex gap-2">
            <span>c</span>
            <input :disabled="temp.dayAndNight"
                   v-model.trim="restaurant.opening_time" type="time" class="form-control form-control-sm"
                   id="restOpenTime">
          </div>
          <div class="flex-fill d-flex gap-2">
            <span>{{ $t('restSettings.to') }}</span>
            <input :disabled="temp.dayAndNight"
                   v-model.trim="restaurant.closing_time" type="time" class="form-control form-control-sm"
                   id="restCloseTime">
          </div>
        </div>
        <div v-else-if="temp.openingTime === 'extended'">
          <div v-for="(day, key) of this.temp.weeklySchedule" class="d-flex gap-2 mb-1">
            <span class="col-1">{{ $t(`restSettings.days.${key}`) }}</span>
            <input v-model.trim="day.opening_time" :id="`weeklySchedule${key}Start`" type="time" class="form-control form-control-sm"> -
            <input v-model.trim="day.closing_time" :id="`weeklySchedule${key}End`" type="time" class="form-control form-control-sm">
          </div>
        </div>
      </div>
    </div>
    <div class="row my-3">
      <div class="col-12 py-2 mb-3 bg-secondary bg-opacity-25">{{ $t('restSettings.sections.media') }}</div>
      <div class="col-12 col-sm-6 mb-3">
        <div class="row">
          <div class="col-3 mb-3">
            <img :src="restaurant.logo_src" class="img-fluid" v-bind:alt="restaurant.title">
          </div>
          <div class="col-9 col-md-9 mb-3">
            <label for="inputRestLogo" class="form-label">{{ $t('restSettings.logo') }}</label>
            <input v-on:change="this.restaurantLogoSelect()" class="form-control" type="file" id="inputRestLogo" multiple>
          </div>
        </div>
      </div>
      <div class="col-12 col-sm-6 mb-3">
        <div class="row">
          <div class="col-3 mb-3">
            <img v-if="restaurant.image_src" :src="restaurant.image_src" class="img-fluid" v-bind:alt="restaurant.title">
            <img v-else src="../../assets/img/placeholder.jpg" class="img-fluid" v-bind:alt="restaurant.title">
          </div>
          <div class="col-9 col-md-9 mb-3">
            <label for="inputRestImg" class="form-label">{{ $t('restSettings.image') }}</label>
            <input v-on:change="this.restaurantImageSelect()" class="form-control" type="file" id="inputRestImg" multiple>
          </div>
        </div>
      </div>
    </div>
    <div class="row my-3">
      <div class="col-12 py-2 mb-3 bg-secondary bg-opacity-25">{{ $t('restSettings.sections.deliveryPayment') }}</div>
      <div class="col-12 col-sm-6">
        <div class="form-check form-switch mb-3">
          <input v-model="restaurant.settings.delivery_enabled" class="form-check-input"
                 type="checkbox" role="switch" id="deliveryEnabled">
          <label class="form-check-label" for="deliveryEnabled">{{ $t('restSettings.deliveryEnabled') }}</label>
        </div>
        <div class="form-check form-switch mb-3">
          <input v-model="restaurant.settings.takeaway_enabled" class="form-check-input"
                 type="checkbox" role="switch" id="takeawayEnabled">
          <label class="form-check-label" for="takeawayEnabled">{{ $t('restSettings.takeawayEnabled') }}</label>
        </div>
        <div v-if="deliveryAndTakeawayDisabled" class="alert alert-warning" role="alert">
          {{ $t('restSettings.deliveryAndTakeawayDisabled') }}
        </div>
      </div>
      <div class="col-12 col-sm-6">
        <div class="form-check form-switch mb-3">
          <input v-model="restaurant.settings.payment_online_enabled" class="form-check-input"
                 type="checkbox" role="switch" id="paymentOnlineEnabled">
          <label class="form-check-label" for="paymentOnlineEnabled">{{ $t('restSettings.paymentMethod.online') }}</label>
        </div>
        <div class="row mb-3">
          <div class="col-auto">
            <select v-model="restaurant.settings.payment_method"
                    :disabled="!restaurant.settings.payment_online_enabled"
                    id="restPaymentMethod" class="form-select form-select-sm">
              <option value="cloudpayments">cloudpayments</option>
            </select>
          </div>
          <div class="col-auto">
            <button class="btn btn-sm btn-outline-secondary"
                    @click="openModalPaymentSettings"
            :disabled="!restaurant.settings.payment_online_enabled || restaurant.settings.payment_method === ''">
              {{ $t('restSettings.paymentMethod.settingsBtn')}}
            </button>
          </div>
          <div v-show="restaurant.settings.payment_online_enabled && restaurant.settings.payment_method !== ''"
               class="col-auto small fst-italic pt-1">
            <span v-if="this.chekPaymentSettingsMethodIsEmpty"
            class="text-success">{{ $t('restSettings.paymentMethod.settingsAdded')}}</span>
            <span v-else
            class="text-danger">{{ $t('restSettings.paymentMethod.settingsEmpty')}}</span>
          </div>
        </div>
        <div class="d-flex justify-content-between flex-wrap pt-3 border-top">
          <div class="form-check form-switch mb-3">
            <input v-model="restaurant.settings.payment_card_enabled" class="form-check-input"
                   type="checkbox" role="switch" id="paymentCardEnabled">
            <label class="form-check-label" for="paymentCardEnabled">{{ $t('restSettings.paymentMethod.card') }}</label>
          </div>
          <div class="form-check form-switch mb-3">
            <input v-model="restaurant.settings.payment_cash_enabled" class="form-check-input"
                   type="checkbox" role="switch" id="paymentCashEnabled">
            <label class="form-check-label" for="paymentCashEnabled">{{ $t('restSettings.paymentMethod.cash') }}</label>
          </div>
        </div>
        <div v-if="AllPaymentDisabled" class="alert alert-danger" role="alert">
          {{ $t('restSettings.paymentMethod.AllDisabledError') }}
        </div>
      </div>
    </div>
    <div class="row my-3">
      <div class="col-12 py-2 mb-3 bg-secondary bg-opacity-25">{{ $t('restSettings.sections.system') }}</div>
      <div class="col-12 col-sm-6 mb-3">
        <div class="mb-3">
          <label for="restCountry" class="form-label">{{ $t('restSettings.country') }} <span
              class="small text-danger">*</span></label>
          <select v-model="restaurant.address_settings.country" id="restCountry" class="form-select">
            <option disabled value="">{{ $t('restSettings.select') }}</option>
            <option v-for="(country, number) in temp.countries"
                    :value="country"
                    :selected="[country === restaurant.address_settings.country]">{{ country }}
            </option>
          </select>
        </div>
        <div>
          <label for="restTimeZone" class="form-label">{{ $t('restSettings.timezone') }} <span
              class="small text-danger">*</span></label>
          <select v-model="restaurant.address_settings.time_zone" id="restTimeZone" class="form-select" aria-describedby="restTimeZoneHelp">
            <option disabled value="">{{ $t('restSettings.select') }}</option>
            <option v-for="(zone, key) in temp.timeZone"
                    :value="zone.name"
                    :selected="[zone.name === restaurant.address_settings.time_zone]">{{ zone.name }} ({{ zone.offset }})
            </option>
          </select>
          <div id="restTimeZoneHelp" class="form-text">{{ $t('restSettings.selectTimeZoneMessage') }}</div>
        </div>
      </div>
      <div class="col-12 col-sm-6 mb-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <p class="form-label">{{ $t('restSettings.notification_emails') }}</p>
          <button class="btn btn-outline-secondary btn-sm" @click="this.addEmailCount" >
            {{ $t('restSettings.add_notification_email') }}
          </button>
        </div>
        <div class="mb-3" v-for="(value, index) in this.notification_emails" >
          <div class="d-flex justify-content-between">
            <input v-model.trim="value.email" type="email" class="form-control"
                   :id="`restOrderNotificationEmails-${index}`"
                   :class="v$.notification_emails.$each.$response.$errors[index].email.length ? 'border-danger' : ''" >
            <button v-if="index > 0" class="btn btn-outline-secondary pt-0 p-2 ms-3" @click="removeAdditionalEmail(index)">
              <svg width="1rem" height="1rem" fill="currentColor">
                <use xlink:href="../../assets/ico/bootstrap-icons.svg#trash3-fill"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="row my-3">
      <div class="col-12 text-end">
        <button :disabled="restaurantUpdating || AllPaymentDisabled"
                v-on:click="changeRestaurant()"
                type="submit" class="btn btn-success ms-3" >
          <span v-show="restaurantUpdating" role="status" aria-hidden="true" class="spinner-border spinner-border-sm"></span>
          {{ $t('restSettings.save') }}
        </button>
      </div>
    </div>

    <!--    modal remove restaurant-->
    <div class="modal fade" id="modalRemoveRestaurant" tabindex="-1" aria-labelledby="modalRemoveRestaurantLabel" aria-hidden="true">
      <div class="modal-dialog modal-sm modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h1 class="modal-title fs-5" id="modalRemoveRestaurantLabel">{{ $t('restSettings.deleteRestaurant') }}</h1>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="text-center">
              <p>{{ $t('restSettings.areYouSureYouWantToDelete') }}<br><b>{{RESTAURANT.title}}</b> ?</p>
              <p>{{ $t('restSettings.restaurantDeleteMessage') }}</p>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">{{ $t('restSettings.no') }}</button>
            <button
                @click="removeRestaurant"
                type="button" class="btn btn-danger">
              <span v-show="restaurantRemoving" role="status" aria-hidden="true" class="spinner-border spinner-border-sm"></span>
              {{ $t('restSettings.yes') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!--    payment settings  -->
    <div class="modal fade" id="modalPaymentSettings" tabindex="-1" aria-labelledby="modalPaymentSettingsLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <cloud-payment
            :settings="cloudPaymentSettings"
            v-on:getSettings="getPaymentSettings"></cloud-payment>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex';
import axios from "axios";
import {Modal} from "bootstrap";
import router from "@/router";
import i18n from "@/i18n";
const config = require('@/config');
// const timeZoneRu = require('@/assets/timezone/timezones_russia.json');
const countries = require('@/assets/countries.json')
import { useVuelidate } from '@vuelidate/core';
import {required, email, helpers } from '@vuelidate/validators';
import cloudPayment from "@/components/paymentMethods/cloudPayment";
import { reactive } from 'vue'
export default {
  name: "RestSettings",
  components: { cloudPayment },
  setup () {
    return { v$: useVuelidate({ $autoDirty: true})}
  },
  data() {
    return {
      removeModal: '',
      temp: {
        dayAndNight: false,
        countries: countries,
        timeZone: [{name: '', offset: 0}],
        openingTime: 'simple',
        weeklySchedule: {
          Monday: {opening_time: '00:00', closing_time: '00:00'},
          Tuesday: {opening_time: '00:00', closing_time: '00:00'},
          Wednesday: {opening_time: '00:00', closing_time: '00:00'},
          Thursday: {opening_time: '00:00', closing_time: '00:00'},
          Friday: {opening_time: '00:00', closing_time: '00:00'},
          Saturday: {opening_time: '00:00', closing_time: '00:00'},
          Sunday: {opening_time: '00:00', closing_time: '00:00'}
        }
      },
      restaurantUpdating: false,
      restaurantRemoving: false,
      selectImgForRest: '',
      selectLogoForRest: '',
      cuisineStr: '',
      restaurant: {
        title: '',
        region: '', //убрать позже
        country: '', //убрать позже
        address: '', //убрать позже
        city: '', //убрать позже
        cuisine: [],
        opening_time: '',
        closing_time: '',
        description: '',
        is_available: false,
        timezone_offset: '', //убрать позже
        image_src: '',
        weekly_schedule: {},
        phone: '',
        settings: {
          delivery_enabled: false,
          takeaway_enabled: false,
          payment_online_enabled: false,
          payment_card_enabled: false,
          payment_cash_enabled: false,
          payment_method: '',
          payment_settings: {},
          notification_emails: [],
        },
        address_settings: {
          time_zone: '',
          country: '',
          region: '',
          city: '',
          address: ''
        }
      },
      notification_emails: [
        {
          email: '',
          new_order: true,
          modified_order: true,
          new_client: false
        }
      ],
    }
  },
  validations() {
    return {
      notification_emails: {
        $each: helpers.forEach ({
          email: { email }
        })
      }
    }
  },
  computed: {
    ...mapGetters([
      'RESTAURANT',
      'COMPANY',
      'LOADING',
    ]),
    restaurantAreLoading() {
      return this.LOADING.restaurantItem
    },
    deliveryAndTakeawayDisabled() {
      if (!this.restaurant.settings.delivery_enabled && !this.restaurant.settings.takeaway_enabled) {
        return true
      } else {
        return false
      }
    },
    AllPaymentDisabled() {
      if (!this.restaurant.settings.payment_online_enabled &&
          !this.restaurant.settings.payment_card_enabled &&
          !this.restaurant.settings.payment_cash_enabled) {
        return true
      } else {
        return false
      }
    },
    checkPaymentSettingsMethod() {
      //проверка есть ли такой метод оплаты в настройках
      const settings = this.restaurant.settings
      if (!settings.payment_settings.hasOwnProperty(settings.payment_method)) { return false }
      return true
    },
    chekPaymentSettingsMethodIsEmpty() {
      //проверка заполнены ли у выбранного метода поля
      if( this.checkPaymentSettingsMethod ) {
        const settings = this.restaurant.settings
        const PaySett  = settings.payment_settings[settings.payment_method]
        if(Object.values(PaySett).length === 0) {return false}
        for (let key in Object.values(PaySett)) {
          if (Object.values(PaySett)[key] === '') return false
        }
        return true
      }
    },
    cloudPaymentSettings() {
      //отправка в компонент данных настроек выбранного метода, которые уже записаны в ресторане
      if (this.checkPaymentSettingsMethod) {
        const settings = this.restaurant.settings
        return settings.payment_settings[settings.payment_method]
      } else {
        return null
      }
    }
  },
  methods: {
    async clearEmails(emails) { //очищает пустые поля email
      for (let index in emails) {
        if (emails[index].email.length === 0) {
          await emails.splice(index, 1)
        } else {
          emails[index].new_order =  true
          emails[index].modified_order = true
          emails[index].new_client = false
        }
      }
      return emails
    },
    async changeRestaurant() {
      await this.$store.dispatch('checkIdToken')
      let idToken = this.$store.state.tokens.idToken;
      let restId = this.$route.params.id
      let axiosHeader = {headers: {"Authorization": idToken, "Content-Type": 'application/json'}}
      if (this.temp.dayAndNight) {
        this.restaurant.opening_time = null
        this.restaurant.closing_time = null
        this.restaurant.weekly_schedule = null
      }
      if (this.temp.openingTime === 'simple') {
        this.clearWeeklySchedule()
        this.restaurant.weekly_schedule = null
      }
      if(this.temp.openingTime === 'extended') {
        this.restaurant.opening_time = null
        this.restaurant.closing_time = null
        this.restaurant.weekly_schedule = this.temp.weeklySchedule
      }
      let data = this.restaurant
      let emails = this.notification_emails
      const trueEmails = await this.clearEmails(emails) //ждем пока проверятся все поля email
      data.settings.notification_emails = trueEmails
      data = JSON.stringify(data)
      this.restaurantUpdating = true
      axios.put(`${config.servUrl}admin/restaurants/${restId}`, data, axiosHeader)
          .then(() => {
            if (this.selectImgForRest) {
              this.addRestaurantImage('restaurant')
            }
            if (this.selectLogoForRest) {
              this.addRestaurantImage('restaurant_logo')
            }
            if (!this.selectImgForRest && !this.selectLogoForRest) {
              this.$store.dispatch('getRestaurantItem', restId)
              this.restaurantUpdating = false
              this.$store.dispatch('addNewNotice', {text: i18n.global.t('restSettings.alertRestaurantSettingsSaved')})
            }
          })
          .catch(error => {
            console.log(error);
            this.restaurantUpdating = false
            this.$store.dispatch('addNewNotice', {text: error, error: true})
          })
    },
    openModalRemoveRestaurant() {
      this.removeModal.show();
    },
    async removeRestaurant() {
      await this.$store.dispatch('checkIdToken')
      this.restaurantRemoving = true
      let idToken = this.$store.state.tokens.idToken;
      let restId = this.$route.params.id
      let axiosHeader = {headers: {"Authorization": idToken, "Content-Type": 'application/json'}}
      axios.delete(`${config.servUrl}admin/restaurants/${restId}`, axiosHeader)
          .then(() => {
            this.restaurantRemoving = false
            this.removeModal.hide();
            this.$store.dispatch('addNewNotice', {text: i18n.global.t('restSettings.alertRestaurantDeleted')})
            setTimeout(() => {
              router.push({path: '/restaurants'})
            }, 500)
          })
          .catch(error => {
            console.log(error);
            this.restaurantUpdating = false
            this.$store.dispatch('addNewNotice', {text: error, error: true})
          })
    },
    restaurantImageSelect() {
      let img = document.querySelector('#inputRestImg').value
      this.selectImgForRest = img
    },
    restaurantLogoSelect() {
      let img = document.querySelector('#inputRestLogo').value
      this.selectLogoForRest = img
    },
    async addRestaurantImage(type) {
      //type = 'restaurant' or 'restaurant_logo'
      await this.$store.dispatch('checkIdToken')
      let idToken = this.$store.state.tokens.idToken;
      let restId = this.$route.params.id
      let fileImage;
      if (type === 'restaurant') {
        fileImage = document.querySelector('#inputRestImg').files[0];
      }
      if (type === 'restaurant_logo') {
        fileImage = document.querySelector('#inputRestLogo').files[0];
      }
      let axiosHeader = {headers: {"Authorization": idToken, "Content-Type": 'multipart/form-data'}}
      const formData = new FormData();
      formData.append('fileContent', fileImage);
      formData.append('entityType', type);
      formData.append('restaurantId', restId);
      axios.post(`${config.servUrl}admin/image-upload`, formData, axiosHeader)
          .then(() => {
            this.$store.dispatch('getRestaurantItem', restId)
            if (type === 'restaurant') {
              this.$store.dispatch('addNewNotice', {text: i18n.global.t('restSettings.alertRestaurantImageUploaded')})
            }
            if (type === 'restaurant_logo') {
              this.$store.dispatch('addNewNotice', {text: i18n.global.t('restSettings.alertRestaurantLogoUploaded')})
            }
            this.restaurantUpdating = false
            this.$store.dispatch('addNewNotice', {text: i18n.global.t('restSettings.alertRestaurantSettingsSaved')})
          })
          .catch(error => {
            console.log(error);
            this.restaurantUpdating = false
            this.$store.dispatch('addNewNotice', {text: error, error: true})
          })
    },
    clearWeeklySchedule() {
      let days = Object.values(this.temp.weeklySchedule)
      days.forEach((e) => {
        e.closing_time = '00:00'
        e.opening_time = '00:00'
      })
    },
    removeAdditionalEmail(index) {
      this.notification_emails.splice(index, 1);
    },
    addEmailCount() {
      this.notification_emails.push({
        email: '',
        new_order: true,
        modified_order: true,
        new_client: false
      })
    },
    async getTimeZones() {
      await this.$store.dispatch('checkIdToken')
      let idToken = this.$store.state.tokens.idToken;
      let axiosHeader = {headers: {"Authorization": idToken, "Content-Type": 'multipart/form-data'}}
      axios.get(`${config.servUrl}admin/timezones/`, axiosHeader)
          .then((response) => {
            this.temp.timeZone = response.data
          })
          .catch(error =>   {
            console.log(error);
            this.$store.dispatch('addNewNotice', {text: error, error: true})
          })
    },
    openModalPaymentSettings() {
      let paymentSettingsModal = new Modal(document.querySelector('#modalPaymentSettings'))
      paymentSettingsModal.show()
    },
    getPaymentSettings(value) {
      let settings = this.restaurant.settings
      let method = this.restaurant.settings.payment_method
      settings.payment_settings = reactive({[method]: value})
    },
  },
  mounted() {
    let restId = this.$route.params.id;
    this.$store.dispatch('getRestaurantItem', restId)
    this.getTimeZones()
    this.removeModal = new Modal(document.querySelector('#modalRemoveRestaurant'))
  },
  watch: {
    'RESTAURANT'(e) {
      this.restaurant = e
      if (e.settings.notification_emails) {
        if (e.settings.notification_emails.length === 0) {
          this.notification_emails = [{email: ''}]
        } else {
          this.notification_emails = e.settings.notification_emails
        }
      }
      this.cuisineStr = e.cuisine.join(', ')
      //добавляем поля в settings, если их там еще нет
      let newSettingKeys = ['delivery_enabled', 'takeaway_enabled', 'payment_online_enabled', 'payment_card_enabled', 'payment_cash_enabled']
      for (let i in newSettingKeys) {
        if (!e.settings[newSettingKeys[i]]) { e.settings[newSettingKeys[i]] = false }
      }
      if (!e.settings.payment_method) { e.settings.payment_method = '' }
      if (!e.settings.payment_settings) { e.settings.payment_settings = {} }
    },
    'restaurant.opening_time'(e) {
      if (e === null && this.RESTAURANT.weekly_schedule === null) {
        this.temp.dayAndNight = true
      }
    },
    'restaurant.weekly_schedule'(e) {
      if (e !== null) {
        this.temp.openingTime = 'extended'
        let days = this.restaurant.weekly_schedule
        for(let key in days) {
          this.temp.weeklySchedule[key] = days[key]
        }
      }
    },
    cuisineStr: {
      handler(str) {
        const newStr = str.replace(/, /gi, ',');
        this.restaurant.cuisine = newStr.split(',')
      }
    },
    'temp.dayAndNight'() {
      this.temp.openingTime = 'simple'
    },
  }
}

</script>

<style scoped>

</style>
